import { useSelectedLogisticsStore } from "@/stores/selected_logistics";
import { useSelectedStore } from "@/stores/selected";

// WATCHERS THAT LAUNCH ACTIONS FROM "SELECTED LOGISTICS" STATE CHANGES

let last_selected_actuals = null   // content key, not the array itself
let last_selected_branch = null
let last_selected_size = null
let last_selected_part_batch = null

// Ticking panels in the tree changes the selection once per tick, and every
// change used to ask the backend for branches, sizes and parts at once. The
// old requests were not cancelled, only ignored, so an operator ticking a dozen
// panels queued a dozen parts lists on the API (13-15 s each, production,
// 2026-10-21). The selection is now loaded once it has been still this long.
const ACTUALS_DEBOUNCE_MS = 500
let actuals_timer = null

export const init_selected_logistics_watchers = function () {
    const slog_store = useSelectedLogisticsStore()


    function loadBranches(uuids) {
        slog_store.setSelectedPartDesign(null)
        slog_store.setBranches([])
        slog_store.setSizes([])
        slog_store.setParts([])
        if (uuids.length != 0) {
            slog_store.loadBranchesByActualUuids(uuids)
        }
        // Branches are derived synchronously from the store, so the selected one
        // can be validated right away. Keeping a branch the new selection does
        // not have left the bar marking nothing and the table empty.
        if (slog_store.selected_branch
            && !slog_store.branches.includes(slog_store.selected_branch)) {
            slog_store.setSelectedBranch(null)
        }
    }

    function loadSizesOrParts(payload) {
        slog_store.setSizes([])
        slog_store.setParts([])
        if (payload.branch === null) {
            return
        } else if (!["timber", "plate"].includes(payload.branch)) {
            slog_store.loadParts(payload)
        } else if (payload.actuals.length != 0) {
            slog_store.loadSizesByActualUuidsAndBranch(payload)
        }
    }

    function loadParts(payload) {
        slog_store.setParts([])
        // A picked batch is a scope of its own: it says which parts are in
        // play, so it needs neither a tree selection nor a size. Without one,
        // an empty selection would ask for every part in the project.
        if (payload.part_batch) {
            slog_store.loadParts(payload)
            return
        }
        if (payload.actuals.length == 0) return
        if (payload.branch == null || payload.size == null) return
        slog_store.loadParts(payload)
    }

    slog_store.$subscribe((mutation, state) => {

        const selected_actuals = state.selected_actuals
        const selected_branch = state.selected_branch
        const selected_size = state.selected_size
        const selected_part_batch = state.selected_part_batch

        // Compared by content, not by identity. `last_selected_actuals` used to
        // hold a reference to the store's own reactive array, so once assigned
        // both names pointed at the same object: if the tree hands us that same
        // array with different items in it, the selection changes, the
        // subscription fires, and the check still says "unchanged" — forever.
        const actuals_key = [...selected_actuals].sort().join(",")

        const payload = () => ({
            actuals: selected_actuals,
            branch: selected_branch,
            size: selected_size,
            part_batch: selected_part_batch,
        })

        if (actuals_key != last_selected_actuals) {
            last_selected_actuals = actuals_key
            clearTimeout(actuals_timer)
            actuals_timer = setTimeout(() => {
                // Read when it fires: the branch or size may have moved meanwhile.
                const now = () => ({
                    actuals: slog_store.selected_actuals,
                    branch: slog_store.selected_branch,
                    size: slog_store.selected_size,
                    part_batch: slog_store.selected_part_batch,
                })
                loadBranches(slog_store.selected_actuals)
                loadSizesOrParts(now())
                loadParts(now())
                // The batches on offer follow the project, not the selection: a
                // batch prepared in the parts list has to be pickable here.
                slog_store.loadSelectablePartBatches(
                    useSelectedStore().selected_project?.uuid)
            }, ACTUALS_DEBOUNCE_MS)
        }

        if (selected_branch != last_selected_branch) {
            loadSizesOrParts(payload())
            loadParts(payload())

            last_selected_branch = selected_branch
        }


        if (selected_size != last_selected_size) {
            loadParts(payload())

            last_selected_size = selected_size
        }

        // Picking a batch narrows the table to its parts; dropping it goes back
        // to the branch and size selection.
        if (selected_part_batch != last_selected_part_batch) {
            loadParts(payload())
            if (selected_part_batch) {
                // Also moves the branch and size selectors onto the batch when
                // it holds a single size, which is the normal case here.
                slog_store.selectBatchScope(selected_part_batch)
            }
            last_selected_part_batch = selected_part_batch
        }

    })

}
